import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { CDateRangePicker } from "@/components/DatePicker/CDateRangePicker";
import { CustomModal } from "@/components/Modal";
import { CMultiSelectWithSpecialOptions } from "@/features/monitoring/components/CMultiSelectWithSpecialOptions";
import { CTextFieldIdentifyName } from "@/components/TextField/CTextFieldIdentifyName";
import { PageTitle } from "@/components/Title";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { useReceptionReasonOptions } from "@/features/realEstateReceptionBookFeed/hooks/useReceptionReasonOptions";
import {
  getSearchMaxDate,
  getSearchMinDate,
  getStartAndEndDate,
} from "@/features/realEstateReceptionBookFeed/utils/dateTime";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PostMonitoringAPI } from "@/features/monitoring/api/PostMonitoringAPI";
import { useApiClient } from "@/hooks/useApiClient";
import { useNavigate } from "react-router-dom";
import { type AxiosResponse, HttpStatusCode, isAxiosError } from "axios";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { CCheckbox } from "@/components/Checkbox/CCheckbox";
import { Label } from "@/components/Label";
import { useMonitoringAmount } from "../hooks/useMonitoringAmount";
import { calculateTermMonth } from "../utils/calculateTermMonth";
import { calculateLimitMonth } from "../utils/calculateLimitMonth";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  chargeCount: number;
  realEstateOwnerAnalyzeRequestId?: number;
  pictureRequestId?: number;
}

/**
 * 実行するボタン押下時の表示されるモーダル。
 * @param {IProps} props
 * @returns
 */
export const ExecuteModal: React.FC<IProps> = (props: IProps) => {
  const {
    open,
    setOpen,
    chargeCount,
    realEstateOwnerAnalyzeRequestId,
    pictureRequestId,
  } = props;

  // 課金確認モーダル開閉制御State
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  // 実行ボタン無効化用State
  const [executeButtonDisabled, setExecuteButtonDisabled] =
    useState<boolean>(true);
  // 確認ボタン押下後のキャンセルボタン無効化State
  const [isPushedConfirmButton, setIsPushedConfirmButton] =
    useState<boolean>(false);
  // 登記原因のState
  const [receptionReasons, setReceptionReasons] = useState<string[]>([]);

  // 登記要因セレクト項目
  const { receptionReasonOptions } = useReceptionReasonOptions({ short: true });

  // 法務局受付日 開始、終了の初期値
  const [startDate, endDate] = getStartAndEndDate();
  const minDate = getSearchMinDate();
  const maxDate = getSearchMaxDate();

  // 料金がかかる旨を通知するためのState
  const [noticeMessage, setNoticeMessage] = useState<string[]>([]);

  // モニタリングに関する金額関連
  const { data: amount } = useMonitoringAmount();

  // React-Hook-Form用スキーマ定義
  const PostDataSchema = z.object({
    identifyName: z
      .string()
      .trim()
      .min(1, { message: "識別名を入力してください" })
      .max(256, { message: "256文字まで入力可能です" }),
    legalAffairsBureauRequestDateRange: z.tuple([
      z.date().min(minDate).max(maxDate),
      z.date().min(minDate).max(maxDate),
    ]),
    notMonthlyUpdatable: z.boolean(),
    receptionReasons: z
      .array(z.string())
      .min(1, "1つ以上登記原因を選択してください"),
  });

  // 定義したZodのスキーマをTypescriptの型に変換
  type PostRequestData = z.infer<typeof PostDataSchema>;

  const reactHookForm = useForm<PostRequestData>({
    mode: "all",
    defaultValues: {
      identifyName: "",
      legalAffairsBureauRequestDateRange: [startDate, endDate],
      notMonthlyUpdatable: false,
      receptionReasons: [],
    },
    resolver: zodResolver(PostDataSchema),
  });

  const showDatePicker = reactHookForm.watch("notMonthlyUpdatable");

  const { apiClient } = useApiClient();
  const navigator = useNavigate();
  const isReadOnly = useReadOnlyUser();

  const [watchNotMonthlyUpdatable, watchLegalAffairsBureauRequestDateRange] =
    reactHookForm.watch([
      "notMonthlyUpdatable",
      "legalAffairsBureauRequestDateRange",
    ]);

  useEffect(() => {
    const legalAffairsBureauRequestDateStart = reactHookForm.getValues(
      "legalAffairsBureauRequestDateRange"
    )[0];
    const legalAffairsBureauRequestDateEnd = reactHookForm.getValues(
      "legalAffairsBureauRequestDateRange"
    )[1];

    if (
      !legalAffairsBureauRequestDateStart ||
      !legalAffairsBureauRequestDateEnd ||
      Number.isNaN(legalAffairsBureauRequestDateStart.getTime()) ||
      Number.isNaN(legalAffairsBureauRequestDateEnd.getTime()) ||
      legalAffairsBureauRequestDateStart > legalAffairsBureauRequestDateEnd
    ) {
      setNoticeMessage([]);
      setExecuteButtonDisabled(true);
      return;
    }

    if (!amount) return;
    let termMonth = calculateTermMonth(
      legalAffairsBureauRequestDateStart,
      legalAffairsBureauRequestDateEnd
    );
    const message = [];
    const unitPrice = amount?.price; // 1件あたりのモニタリング金額
    const maximumPrice = amount?.maximum; // 1件あたりのモニタリング課金額上限
    if (reactHookForm.getValues("notMonthlyUpdatable")) {
      const limitMonth = calculateLimitMonth(unitPrice, maximumPrice);
      if (termMonth > limitMonth) {
        termMonth = limitMonth;
      }
      const totalCost = (chargeCount * termMonth * unitPrice).toLocaleString(
        "ja-JP"
      );
      message.push(`注意: 実行すると${totalCost}円の費用が発生します。`);
      message.push(
        `過去分1物件あたりの上限金額は${maximumPrice.toLocaleString(
          "ja-JP"
        )}円です。`
      );
    } else {
      const monthlyCost = (chargeCount * unitPrice).toLocaleString("ja-JP");
      message.push(
        `注意: 実行すると毎月最大${monthlyCost}円の費用が発生します。`
      );
    }
    setNoticeMessage(message);
    setExecuteButtonDisabled(false);
  }, [
    watchNotMonthlyUpdatable,
    watchLegalAffairsBureauRequestDateRange,
    open,
    chargeCount,
    amount,
  ]);

  // モニタリングするを押下した時の処理
  const handlePushedConfirmButton = (): void => {
    setIsConfirmModalOpen(true);
  };

  // 確認モーダルではいを選択した時の処理
  const handleExecuteSubmit = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    // NOTE: 月初日を取得するために、月を1進めて1日目を指定する
    const legalAffairsBureauRequestDateStart = new Date();
    legalAffairsBureauRequestDateStart.setFullYear(
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[0]
        .getFullYear(),
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[0]
        .getMonth(),
      1
    );

    // NOTE: 月末日を取得するために、月を1進めて0日目を指定する
    const legalAffairsBureauRequestDateEnd = new Date();
    legalAffairsBureauRequestDateEnd.setFullYear(
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[1]
        .getFullYear(),
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[1]
        .getMonth() + 1,
      0
    );

    setIsPushedConfirmButton(true);
    PostMonitoringAPI(apiClient, {
      name: reactHookForm.getValues("identifyName"),
      receptionReasonIds: reactHookForm.getValues("receptionReasons"),
      realEstateOwnerAnalyzeRequestId,
      pictureRequestId,
      monthlyUpdatable: !reactHookForm.getValues("notMonthlyUpdatable"),
      legalAffairsBureauRequestDateStart,
      legalAffairsBureauRequestDateEnd,
    })
      .then((response) => {
        navigator(`/monitoring/${response.id}/`);
      })
      .catch((error) => {
        if (
          isAxiosError<AxiosResponse>(error) &&
          (error.response?.status === HttpStatusCode.BadRequest ||
            error.response?.status === HttpStatusCode.NotFound)
        ) {
          toast.error("モニタリング登録ができませんでした。");
          return;
        }
        if (
          isAxiosError<AxiosResponse>(error) &&
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          toast.error(
            "モニタリング登録に失敗しました。しばらく経ってから再度お試しください。"
          );
        }
      })
      .finally(() => {
        reactHookForm.reset();
        cancelLoading();
        setIsPushedConfirmButton(false);
        setIsConfirmModalOpen(false);
        setOpen(false);
      });
  };

  return (
    <>
      <CustomModal
        isOpen={open}
        handleClose={(
          event: Record<string, unknown>,
          reason: "backdropClick" | "escapeKeyDown"
        ): void => {
          if (
            reason &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          setOpen(false);
        }}
        ariaLabelledby="execute-modal"
        ariaDescribedby="execute-modal-description"
        maxHeight={"90vh"}
        overflow="scroll"
      >
        <Stack direction={"column"} spacing={3}>
          {/* タイトル */}
          <PageTitle>モニタリング登録内容 設定・確認</PageTitle>

          {/* メッセージ */}
          <Typography>モニタリング定義を設定してください。</Typography>

          <Grid container spacing={2} justifyContent={"center"}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={5.5}>
                  <Label required={true}>識別名</Label>
                  <CTextFieldIdentifyName
                    name={"identifyName"}
                    control={reactHookForm.control}
                    size={SizingWrapperStyle.LARGE}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6.5}>
                  <Label required={true}>登記原因</Label>
                  <CMultiSelectWithSpecialOptions<PostRequestData>
                    name={"receptionReasons"}
                    control={reactHookForm.control}
                    label="登記原因"
                    options={receptionReasonOptions}
                    value={receptionReasons}
                    onChange={setReceptionReasons}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  rowGap: 2,
                  pt: 1,
                  pb: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <CCheckbox<PostRequestData>
                  name={"notMonthlyUpdatable"}
                  control={reactHookForm.control}
                  label={"月次でモニタリングしない"}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  rowGap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <CDateRangePicker<PostRequestData>
                  name={"legalAffairsBureauRequestDateRange"}
                  control={reactHookForm.control}
                  startDateLabel="法務局受付日 開始"
                  endDateLabel="法務局受付日  終了"
                  minDate={minDate}
                  maxDate={maxDate}
                  defaultCalendarMonth={startDate}
                  disabled={!showDatePicker}
                  views={["year", "month"]}
                  inputFormat={"yyyy/MM"}
                  mask={"____/__"}
                  displayDateLabel={true}
                  requiredEnd={showDatePicker}
                  requiredStart={showDatePicker}
                />
              </Box>
            </Grid>
          </Grid>

          {/* 通知メッセージ */}
          {noticeMessage.length > 0 && (
            <Grid item xs={12}>
              <Typography textAlign={"center"} color={"error"}>
                {noticeMessage.map((message, idx) => (
                  <div key={idx}>{message}</div>
                ))}
              </Typography>
            </Grid>
          )}

          {/* ボタン */}
          <Stack
            direction={"row"}
            spacing={2}
            marginTop={2}
            justifyContent={"center"}
          >
            <Button
              label="キャンセル"
              onClick={() => {
                reactHookForm.reset();
                setOpen(false);
              }}
              size={SizingWrapperStyle.SMALL}
              variant={ButtonVariantOption.Outlined}
            />
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <Button
                label="モニタリングする"
                onClick={handlePushedConfirmButton}
                disabled={
                  isReadOnly ||
                  !reactHookForm.formState.isValid ||
                  executeButtonDisabled
                }
                size={SizingWrapperStyle.SMALL}
                variant={ButtonVariantOption.Contained}
              />
            </ReadOnlyTooltip>
          </Stack>
        </Stack>
      </CustomModal>
      {/* 課金確認モーダル */}
      <CustomModal
        isOpen={isConfirmModalOpen}
        handleClose={(
          event: Record<string, unknown>,
          reason: "backdropClick" | "escapeKeyDown"
        ): void => {
          if (
            reason &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          setIsConfirmModalOpen(false);
        }}
        ariaLabelledby="confirm-modal"
        ariaDescribedby="confirm-modal-description"
      >
        <PageTitle>確認</PageTitle>
        <Box>
          <Typography>課金が発生します。よろしいですか？</Typography>

          <Box
            sx={{
              mr: 2,
              my: 3,
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              label="いいえ"
              disabled={isPushedConfirmButton}
              onClick={() => {
                setIsConfirmModalOpen(false);
              }}
              variant={ButtonVariantOption.Outlined}
            />
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <SubmitButton
                label={"はい"}
                timeout={120_000} // ms
                onClick={handleExecuteSubmit}
                variant={ButtonVariantOption.Contained}
                disabled={isReadOnly}
              />
            </ReadOnlyTooltip>
          </Box>
        </Box>
      </CustomModal>
    </>
  );
};
